/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { Box, DialogContentText, TableCell, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  DialogDefaultV2,
  DropzoneModalV2,
  TableWidgetV2,
  UIconV2,
} from '../../../../components/v2';
import MtBtNsmpModal from './MtBtNsmpModal';
import DigitacaoResumoStrip from './components/DigitacaoResumoStrip';
import ParticipacaoBanner from './components/ParticipacaoBanner';
import ReatribuidaDialog from './components/ReatribuidaDialog';
import IntegracaoChip from './components/IntegracaoChip';
import MinhaDigitacaoCell from './components/MinhaDigitacaoCell';
import ServicoCell from '../components/ServicoCell';
import {
  getTipoServicoLabel,
} from '../utils/servicosFilters';
import MtBtQuickFilters from './components/MtBtQuickFilters';
import { computeResumoNsir } from './utils/computeResumoNsir';
import { useParticipacaoMode } from './hooks/useParticipacaoMode';
import { useMtBtDigitacaoContext } from './hooks/useMtBtDigitacaoContext';
import { useMtBtSave } from './hooks/useMtBtSave';
import { isAnaliseRisco, isFotoInicio, isFotoFim, isServiceRow } from '../utils/nsmpRowTypes';
import {
  getServicosShowEdit,
  getServicosShowView,
  getServicosShowViewRow,
  isDigitadorEmDigitacao,
} from '../utils/servicosPermissions';
import * as selectorsAuth from '../../../../store/auth/reducer';
import * as actionsNsmp from '../../../../store/nsmp/actions';
import * as Status from '../../../../constants/status';
import * as Role from '../../../../constants/role';
import { canViewDigitadorReport } from '../../../../helpers/digitadorReportPermission';
import { isBlankString } from 'utils';
import { useNsmpUrlSync } from '../hooks/useNsmpUrlSync';
import {
  applyMtBtQuickFilters,
  EMPTY_MTBT_QUICK_FILTER,
  MTBT_STATUS_CHIPS,
} from './utils/mtbtServicosFilters';

const MtBtServicesTab = ({
  nsmps,
  nsirId,
  updateNsmp,
  saveNsmp,
  digitarNsmp,
  cleanSelectedNsmp,
  nsirStatus,
  userRole,
  deleteNsmp,
  authData,
  userId,
  inspetorId,
  digitadorId,
  nsirTitle,
  tipoServicoId,
  cloneNsmp,
  deleteCloneNsmp,
  history,
  location,
  nsirData,
}) => {
  const [quickFilter, setQuickFilter] = useState(EMPTY_MTBT_QUICK_FILTER);
  const [open, setOpen] = useState(false);
  const [openFotoInicial, setOpenFotoInicial] = useState(false);
  const [openFotoFinal, setOpenFotoFinal] = useState(false);
  const [openAnaliseRisco, setOpenAnaliseRisco] = useState(false);
  const [openClone, setOpenClone] = useState(false);
  const [openDeleteClone, setOpenDeleteClone] = useState(false);
  const [cloneId, setCloneId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [readOnly, setReadOnly] = useState(false);

  const [reatribuidaDialog, setReatribuidaDialog] = useState({ open: false, message: '' });

  const { context: digitacaoContext, enrichedNsmps } = useMtBtDigitacaoContext(nsirId, nsmps);

  const nsirDataEffective = useMemo(() => ({
    ...(nsirData || { digitadorId, status: nsirStatus }),
    participacaoUsuario: digitacaoContext?.participacaoUsuario
      ?? nsirData?.participacaoUsuario,
  }), [nsirData, digitadorId, nsirStatus, digitacaoContext?.participacaoUsuario]);

  const participacaoMode = useParticipacaoMode({
    nsirData: nsirDataEffective,
    digitacaoContext,
    userId,
    userRole,
    nsmps: enrichedNsmps,
  });

  const resumoNsir = useMemo(
    () => computeResumoNsir(enrichedNsmps, {
      userId,
      mode: participacaoMode,
      serverResumo: digitacaoContext?.resumoNsirDigitacao,
    }),
    [enrichedNsmps, userId, participacaoMode, digitacaoContext?.resumoNsirDigitacao],
  );

  const isAdmin = () => authData && (authData.role === Role.ADMIN || authData.role === Role.SUPER_ADMIN);
  const showAuditoriaColumn = isAdmin();
  const isConsulta = participacaoMode === 'CONSULTA';
  const showRelatorioLink =
    userRole === Role.DIGITADOR
    && canViewDigitadorReport({ authData, userRole });
  const compactAdminView = isAdmin() && !isConsulta;

  const filteredRows = useMemo(
    () => applyMtBtQuickFilters(enrichedNsmps || [], quickFilter, { userId, mode: participacaoMode }),
    [enrichedNsmps, quickFilter, userId, participacaoMode],
  );

  const handleNsmpNotInFilter = useCallback(
    () => setQuickFilter(EMPTY_MTBT_QUICK_FILTER),
    [],
  );

  const { openNsmpModal, closeNsmpModal, syncCarouselNsmpId } = useNsmpUrlSync({
    history,
    location,
    enabled: !!nsirId,
    allRows: enrichedNsmps,
    filteredRows,
    open,
    selectedId,
    setOpen,
    setSelectedId,
    setCurrentIndex,
    setReadOnly,
    cleanSelectedNsmp,
    onNsmpNotInFilter: handleNsmpNotInFilter,
  });

  const handlerCancel = () => {
    closeNsmpModal();
  };

  const goToNext = () => {
    if (currentIndex !== null && currentIndex < filteredRows.length - 1) {
      const next = filteredRows[currentIndex + 1];
      setCurrentIndex(currentIndex + 1);
      setSelectedId(next.id);
      syncCarouselNsmpId(next.id);
    }
  };

  const goToPrevious = () => {
    if (currentIndex !== null && currentIndex > 0) {
      const prev = filteredRows[currentIndex - 1];
      setCurrentIndex(currentIndex - 1);
      setSelectedId(prev.id);
      syncCarouselNsmpId(prev.id);
    }
  };

  const closeModalAfterSave = useCallback(
    (_result, { advanceNext } = {}) => {
      if (advanceNext) {
        goToNext();
      } else {
        closeNsmpModal();
      }
    },
    [closeNsmpModal, currentIndex, filteredRows, syncCarouselNsmpId],
  );

  const { savePayload } = useMtBtSave({
    nsirStatus,
    digitadorId,
    userId,
    userRole,
    nsirId,
    digitarNsmp,
    updateNsmp,
    saveNsmp,
    onSuccess: closeModalAfterSave,
    onReatribuida: (message) => {
      setReatribuidaDialog({ open: true, message });
    },
  });

  const handlerConfirm = (data) => {
    savePayload(data);
  };

  const handlerConfirmAndNext = (data) => {
    savePayload(data, { advanceNext: true });
  };

  const onEdit = (id) => {
    if (isConsulta) {
      onView(id);
      return;
    }
    const index = filteredRows.findIndex((r) => r.id === id);
    openNsmpModal({
      id,
      readOnly: false,
      index,
    });
  };

  const onView = (id) => {
    const index = filteredRows.findIndex((r) => r.id === id);
    openNsmpModal({
      id,
      readOnly: true,
      index,
    });
  };

  const handleOpenNsmpFromIntegracao = useCallback((id) => {
    const index = filteredRows.findIndex((r) => r.id === id);
    openNsmpModal({
      id,
      readOnly: false,
      index,
    });
  }, [filteredRows, openNsmpModal]);

  const onAdd = () => {
    openNsmpModal({
      id: null,
      readOnly: false,
      index: null,
    });
  };

  const hasNext = currentIndex !== null && currentIndex < filteredRows.length - 1;
  const hasPrevious = currentIndex !== null && currentIndex > 0;
  const carouselPosition = currentIndex !== null
    ? { current: currentIndex + 1, total: filteredRows.length }
    : null;

  const tableTitles = (() => {
    const base = ['', 'N° serviço', 'Prioridade', 'Serviço', 'Tipo Serviço', 'Nsmps'];
    if (!isConsulta) {
      base.push('Integração');
    }
    if (isConsulta) {
      base.push('Minha digitação');
    }
    if (showAuditoriaColumn) {
      base.push('Auditoria');
    }
    return base;
  })();

  const applyQuickFilterChip = (chip) => {
    setQuickFilter((prev) => ({
      ...prev,
      mtbtChip: prev.mtbtChip === chip ? null : chip,
    }));
  };

  const getHeaderActions = () => {
    const actions = [];
    if (nsirStatus === Status.EM_INSPECAO && filteredRows.filter((row) => isFotoInicio(row)).length === 0) {
      actions.push({ title: 'Foto Inicial', onClick: () => setOpenFotoInicial(true) });
    }
    if (nsirStatus === Status.EM_INSPECAO && filteredRows.filter((row) => isFotoFim(row)).length === 0) {
      actions.push({ title: 'Foto Final', onClick: () => setOpenFotoFinal(true) });
    }
    if (nsirStatus === Status.EM_INSPECAO && filteredRows.filter((row) => isAnaliseRisco(row)).length === 0) {
      actions.push({ title: 'Análise de Risco', onClick: () => setOpenAnaliseRisco(true) });
    }
    return actions;
  };

  const getRowTitle = (nsmp) => {
    if (isFotoInicio(nsmp)) return 'Foto Inicial';
    if (isFotoFim(nsmp)) return 'Foto Final';
    if (isAnaliseRisco(nsmp)) return 'Análise de Risco';
    if (nsmp.fotoRegistro) return `Foto Registro ${nsmp.codigo}`;
    return `Serviço ${nsmp.codigo}`;
  };

  const renderRows = (row) => {
    if (row.codigo === '-999' && row.tipoServico === 'FO') {
      return (
        <>
          <TableCell />
          <TableCell>Serviço Georreferenciado</TableCell>
          <TableCell>-</TableCell>
          <TableCell><ServicoCell value={row.servico} /></TableCell>
          <TableCell>{getTipoServicoLabel(row)}</TableCell>
          <TableCell>-</TableCell>
          {!isConsulta && <TableCell>-</TableCell>}
          {isConsulta && <TableCell>-</TableCell>}
          {showAuditoriaColumn && <TableCell>-</TableCell>}
        </>
      );
    }

    return (
      <>
        <TableCell sx={{ width: 20 }}>
          {row.prioridade && String(row.prioridade).toLowerCase() === '1' ? <UIconV2 /> : ''}
        </TableCell>
        <TableCell>{getRowTitle(row)}</TableCell>
        <TableCell>{row.prioridade}</TableCell>
        <TableCell><ServicoCell value={row.servico} /></TableCell>
        <TableCell>{getTipoServicoLabel(row)}</TableCell>
        <TableCell>
          {row.tipoServico && (
            <>
              <Typography variant="caption" fontWeight={700}>N° Nsmp Programada: </Typography>
              <Typography variant="caption">{isBlankString(row.nsmpProgramada)}</Typography>
              <br />
              <Typography variant="caption" fontWeight={700}>N° Nsmp Nova: </Typography>
              <Typography variant="caption">{isBlankString(row.nsmp)}</Typography>
            </>
          )}
        </TableCell>
        {!isConsulta && (
          <TableCell>
            {isServiceRow(row) ? (
              <IntegracaoChip
                row={row}
                onOpenNsmp={handleOpenNsmpFromIntegracao}
                participacaoMode={participacaoMode}
                userId={userId}
              />
            ) : ''}
          </TableCell>
        )}
        {isConsulta && (
          <TableCell>
            {isServiceRow(row) ? <MinhaDigitacaoCell row={row} userId={userId} /> : ''}
          </TableCell>
        )}
        {showAuditoriaColumn && (
          <TableCell>
            {isServiceRow(row) && row.auditoria === true ? (
              <CheckCircleIcon fontSize="small" color="primary" />
            ) : ''}
          </TableCell>
        )}
      </>
    );
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  const getFotos = async (fotos) => {
    const fotosList = [];
    for (let i in fotos) {
      fotosList.push({
        id: uuidv4(),
        nome: fotos[i].name,
        image: await toBase64(fotos[i]),
      });
    }
    return fotosList;
  };

  const isInspetor = () => nsirStatus === Status.EM_INSPECAO && inspetorId === userId;
  const isDigitador = () => isDigitadorEmDigitacao({
    nsirStatus,
    digitadorId,
    userId,
    userRole,
  });

  const permissionsCtx = {
    nsirStatus,
    inspetorId,
    userId,
    userRole,
    digitadorId,
    tipoServicoId,
    isAdmin: isAdmin(),
  };

  const showEdit = getServicosShowEdit(permissionsCtx);
  const showView = getServicosShowView(permissionsCtx);
  const showViewRow = (row) => getServicosShowViewRow(row, {
    ...permissionsCtx,
    isServiceRow,
  });

  const showEditEffective = isConsulta ? false : showEdit;
  const showViewEffective = isConsulta ? true : showView;

  const handleReatribuidaClose = () => {
    setReatribuidaDialog({ open: false, message: '' });
    closeNsmpModal();
    history.push('/v2/nsirs');
  };

  const digitadorAtualNome =
    nsirDataEffective?.participacaoUsuario?.digitadorAtual?.nome
    || nsirData?.digitadorNome
    || null;

  return (
    <>
      <Box>
        <ParticipacaoBanner
          mode={participacaoMode}
          nsirData={nsirDataEffective}
          meusDigitados={resumoNsir.meusDigitados}
        />
        <DigitacaoResumoStrip
          resumo={resumoNsir}
          mode={participacaoMode}
          compactAdminView={compactAdminView}
          showRelatorioLink={showRelatorioLink}
          digitadorAtualNome={digitadorAtualNome}
          onFilterIntegracao={() => applyQuickFilterChip(null)}
          onFilterErro={() => applyQuickFilterChip(MTBT_STATUS_CHIPS.ERRO_INTEGRACAO)}
        />
        <MtBtQuickFilters
          value={quickFilter}
          onChange={setQuickFilter}
          totalCount={(enrichedNsmps || []).length}
          filteredCount={filteredRows.length}
          showDigitadosPorMim={isConsulta}
        />
        <TableWidgetV2
          rows={filteredRows}
          titles={tableTitles}
          headerTitle={`Serviços > ${nsirTitle}`}
          renderRows={renderRows}
          showEditRow={isServiceRow}
          hasRowActions
          onEdit={onEdit}
          onAdd={onAdd}
          onView={onView}
          onDelete={(id) => deleteNsmp(id)}
          showDelete={isInspetor()}
          showEdit={showEditEffective}
          showView={showViewEffective}
          showViewRow={showViewRow}
          hasAdd={nsirStatus === Status.EM_INSPECAO}
          extraHeaderActions={getHeaderActions()}
          onClearFilters={() => setQuickFilter(EMPTY_MTBT_QUICK_FILTER)}
          actions={[
            {
              key: 'clone',
              icon: <AddIcon />,
              show: (row) => !isConsulta && isDigitador() && isServiceRow(row) && nsirStatus === Status.EM_DIGITACAO,
              onClick: (id) => {
                setOpenClone(true);
                setCloneId(id);
              },
            },
            {
              key: 'delete',
              icon: <DeleteIcon />,
              show: (row) => !isConsulta && row.idNsmpReference && nsirStatus === Status.EM_DIGITACAO,
              onClick: (id) => {
                setOpenDeleteClone(true);
                setCloneId(id);
              },
            },
          ]}
        />
      </Box>

      <MtBtNsmpModal
        open={open}
        onCancel={handlerCancel}
        onConfirm={handlerConfirm}
        onConfirmAndNext={handlerConfirmAndNext}
        selectedId={selectedId}
        readOnly={readOnly || isConsulta}
        nsirTitle={nsirTitle}
        onNext={goToNext}
        onPrevious={goToPrevious}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        carouselPosition={carouselPosition}
        participacaoMode={participacaoMode}
        contextNsmps={enrichedNsmps}
      />

      <ReatribuidaDialog
        open={reatribuidaDialog.open}
        message={reatribuidaDialog.message}
        onClose={handleReatribuidaClose}
      />

      <DropzoneModalV2
        onClose={() => setOpenFotoInicial(false)}
        open={openFotoInicial}
        onSave={async (fotos) => {
          setOpenFotoInicial(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '000',
            fotoInicio: true,
            fotoFim: false,
            analiseRisco: false,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />
      <DropzoneModalV2
        onClose={() => setOpenFotoFinal(false)}
        open={openFotoFinal}
        onSave={async (fotos) => {
          setOpenFotoFinal(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '999',
            fotoInicio: false,
            fotoFim: true,
            analiseRisco: false,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />
      <DropzoneModalV2
        onClose={() => setOpenAnaliseRisco(false)}
        open={openAnaliseRisco}
        onSave={async (fotos) => {
          setOpenAnaliseRisco(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '-001',
            fotoInicio: false,
            fotoFim: false,
            analiseRisco: true,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />

      <DialogDefaultV2
        open={openClone}
        handlerCancel={() => { setOpenClone(false); setCloneId(null); }}
        handlerConfirm={() => {
          cloneNsmp(cloneId, () => {
            setCloneId(null);
            setOpenClone(false);
          });
        }}
        title="Você tem certeza que deseja clonar o serviço??"
        confirmMessage="Clonar"
      >
        <DialogContentText>
          Ao clonar o serviço, todas as informações e fotos serão duplicadas. O novo serviço terá um novo código.
        </DialogContentText>
      </DialogDefaultV2>

      <DialogDefaultV2
        open={openDeleteClone}
        handlerCancel={() => { setOpenDeleteClone(false); setCloneId(null); }}
        handlerConfirm={() => {
          deleteCloneNsmp(cloneId, () => {
            setOpenDeleteClone(false);
            setCloneId(null);
          });
        }}
        title="Você tem que deseja remover o serviço?"
        confirmMessage="Remover"
      >
        <DialogContentText>
          A informação será removida e não poderá ser mais recuperado.
        </DialogContentText>
      </DialogDefaultV2>
    </>
  );
};

MtBtServicesTab.propTypes = {
  nsmps: PropTypes.array,
  nsirId: PropTypes.string,
  updateNsmp: PropTypes.func.isRequired,
  saveNsmp: PropTypes.func.isRequired,
  digitarNsmp: PropTypes.func.isRequired,
  cleanSelectedNsmp: PropTypes.func.isRequired,
  nsirStatus: PropTypes.string,
  userRole: PropTypes.string,
  deleteNsmp: PropTypes.func.isRequired,
  authData: PropTypes.object,
  userId: PropTypes.string,
  inspetorId: PropTypes.string,
  digitadorId: PropTypes.string,
  nsirTitle: PropTypes.string,
  tipoServicoId: PropTypes.string,
  cloneNsmp: PropTypes.func.isRequired,
  deleteCloneNsmp: PropTypes.func.isRequired,
  history: PropTypes.object,
  location: PropTypes.object,
  nsirData: PropTypes.object,
};

const mapStateToProps = (state) => ({
  userRole: selectorsAuth.getRole(state),
  userId: selectorsAuth.getUserId(state),
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsNsmp }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(MtBtServicesTab);
